import type { UploadKind } from '@trafiku/contracts';
import { ImagePlus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ApiError, uploadImage } from '@/lib/api';
import { t } from '@/lib/i18n';
import { Button } from './ui/button';

/** Uploads an image to the given folder and returns its public URL via onChange. */
export function ImageUpload({
  kind,
  value,
  onChange,
  square,
}: {
  kind: UploadKind;
  value?: string | null;
  onChange: (url: string) => void;
  square?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadImage(kind, file));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('Upload failed'));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <div className="flex items-start gap-3">
      <div
        className={`flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50 ${square ? 'size-20' : 'h-20 w-32'}`}
      >
        {value ? (
          <img src={value} alt="" className="h-full w-full object-cover" />
        ) : (
          <ImagePlus className="size-6 text-slate-400" />
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <input
          ref={input}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => pick(e.target.files?.[0])}
        />
        <Button size="sm" variant="secondary" loading={busy} onClick={() => input.current?.click()}>
          {value ? t('Replace') : t('Upload')}
        </Button>
        {value && (
          <Button size="sm" variant="ghost" icon={<X className="size-4" />} onClick={() => onChange('')}>
            {t('Remove')}
          </Button>
        )}
      </div>
    </div>
  );
}
